import { dayZeroStopIdSchema, PULSE_BEHAVIOURS, pulseAnswersSchema, pulseBehaviourSchema, } from "@healthos/shared";
import { z } from "zod";
import { AmbientStateSchema } from "../ambient.js";
import { ianaTimezoneSchema, timezoneCheckedProfileData } from "../timezone.js";
import { RouteMilestoneCelebration } from "./challenges.js";
export const AmbientResponse = AmbientStateSchema;
/**
 * Which client is asking. Only surfaces that behave differently per client may
 * read this, and today that is exactly one thing: the Day Zero tour, whose
 * stops navigate to web screens and so are never served to mobile.
 *
 * Absent means web. Adding a value here is a promise that some surface treats
 * it differently, so do not add one speculatively.
 */
export const ClientSurface = z.enum(["web", "mobile", "extension"]);
export const AmbientQuery = z.object({
    client: ClientSurface.optional(),
});
export const AttentionEventIdParams = z.object({
    id: z.string().min(1),
});
/**
 * How the surface judged the moment it was shown in. Optional and additive:
 * absent for every client that does not measure it, and nothing may branch on
 * it. It annotates an outcome, it is never an input to a decision.
 */
export const AttentionMoment = z.enum([
    "returned",
    "boundary",
    "gap",
    "in-meeting",
    "heads-down",
    "unknown",
]);
export const ConsumeAttentionQuery = z.object({
    moment: AttentionMoment.optional(),
});
export const ConsumeAttentionResponse = z.object({
    ok: z.boolean(),
    consumed: z.boolean(),
});
export const AmbientNotNowRequest = z.object({
    actionId: z.string().min(1).nullable().optional(),
    kind: z.string().min(1).nullable().optional(),
    // The dismissed microstep's id, when the dismissed card is a microstep.
    // Lets the dismissal signal be attributed to a specific step on the today
    // surface, the way the Slack path already does. Null for non-microstep cards.
});
export const AmbientNotNowResponse = z.object({
    ok: z.boolean(),
});
export const CompanionAppPromptDismissResponse = z.object({
    ok: z.boolean(),
});
// Hold pushes for a while without turning them off. Quiet hours are a standing
// schedule; this is "not right now", the control members actually reach for.
// Zero lifts the hold.
export const PUSH_SNOOZE_HOURS = [0, 1, 2, 4, 8];
export const PushSnoozeRequest = z.object({
    hours: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(4), z.literal(8)]),
});
export const PushSnoozeResponse = z.object({
    snoozedUntil: z.string().nullable(),
});
export const OutcomeResponse = z.record(z.string(), z.unknown());
export const ScoreResponse = z.record(z.string(), z.unknown());
export const WhoamiResponse = z.object({
    userId: z.string(),
    /**
     * The covered entity this member belongs to. Server-authoritative
     * (`users.company_id`, JWT `companyId` claim as the fallback) because the
     * clients cannot see it reliably: the claim rides the access token, and the
     * desktop / Teams host-auth profile carries neither token nor claim.
     *
     * Clients stamp it onto Datadog RUM as a user property, which is what lets
     * every view, action, error, and crash be grouped by customer. An opaque
     * tenant uuid, never a company name, so it stays PHI-free.
     */
    companyId: z.string().nullable().optional(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable().optional(),
    email: z.unknown().nullable(),
    persona: z.string().nullable(),
    lifeContext: z.enum(["default", "low_agency"]),
    onboardingComplete: z.boolean(),
    /**
     * Companion product seat (`user:healthos:base`). Independent of the API
     * ticket (`user:healthos`) and of Contact Centre. The clients route from
     * this flag; they must not infer Companion from onboarding complete.
     */
    hasCompanionAccess: z.boolean(),
    /**
     * Contact Centre product seat. Same fact GET /api/contact-centre/me uses
     * (`cc:view`), so a gate can settle both products from one whoami payload.
     */
    hasContactCentreAccess: z.boolean(),
    /**
     * When the member finished onboarding, which is day zero for retention.
     * Server-authoritative on purpose: a client-side "first seen" would reset on
     * a new browser and make a returning member look brand new.
     */
    onboardingCompletedAt: z.string().nullable().optional(),
    hasPlan: z.boolean(),
    demoDay: z.number().int().min(1).nullable().optional(),
    cyclePhase: z.enum(["active", "reflection"]).nullable().optional(),
    planId: z.string().nullable().optional(),
    planDuration: z.number().int().min(1).nullable().optional(),
});
export const TweaksPanelResponse = z.object({
    enabled: z.boolean(),
});
export const DevPushAllResponse = z.object({
    ok: z.boolean(),
    total: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    results: z.array(z.object({
        id: z.string(),
        status: z.enum(["sent", "failed"]),
    })),
});
export const DevJourneyProgressRequest = z.object({
    amount: z.number().positive().max(100_000),
    unit: z.enum(["steps", "kilometers"]),
});
export const DevJourneyProgressResponse = z.object({
    enrollmentId: z.string().uuid(),
    journeyName: z.string(),
    addedSteps: z.number().int().positive(),
    progress: z.number().int().nonnegative(),
    target: z.number().int().positive(),
    completed: z.boolean(),
});
/**
 * Dev replay of the route waypoint celebration against the member's own active
 * route. Forgets any prior claim so the moment can be seen again, and returns a
 * real celebration, so the share button publishes for real.
 */
export const DevRouteWaypointResponse = z.object({
    celebration: RouteMilestoneCelebration,
});
export const DevSeedChallengesResponse = z.object({
    activities: z.number().int().nonnegative(),
    groups: z.number().int().nonnegative(),
    leaderboards: z.number().int().nonnegative(),
    teammates: z.number().int().nonnegative(),
});
/** Dev only: who the caller plays in a coaching demo, and which kind of programme. */
export const DevSeedCoachingRequest = z.object({
    as: z.enum(["member", "professional"]),
    kind: z.enum(["coaching", "care_team"]),
    /** False takes the caller off that kind of programme, as a member. */
    on: z.boolean().default(true),
});
export const DevCoachingMomentRequest = z.object({
    moment: z.enum(["prep", "reminder", "follow_up", "action"]),
    kind: z.enum(["coaching", "care_team"]).default("coaching"),
});
export const DevCoachingMomentResponse = z.object({ shown: z.boolean() });
export const DevSeedPastChallengesResponse = z.object({
    challenges: z.number().int().nonnegative(),
    participants: z.number().int().nonnegative(),
});
export const DevActivateCompanyRouteVoteResponse = z.object({
    active: z.boolean(),
});
export const DevSeedStepHistoryResponse = z.object({
    daysSeeded: z.number().int().positive(),
    streakDays: z.number().int().nonnegative(),
    targetSteps: z.number().int().positive(),
});
export const DevSeedRequest = z.object({
    persona: z.string(),
    lifeContext: z.enum(["default", "low_agency"]).optional(),
});
export const DevSeedResponse = z.record(z.string(), z.unknown());
export const DevFillLens = z.enum([
    "glp1",
    "sleep",
    "cardio",
    "stress",
    "movement",
    "nutrition",
    "hydration",
    "connection",
]);
/** How many days of history a lens fill backfills. 6 (the longstanding
 * default) or 14, matching Logbook's own 7/14/28-day detail-range toggle —
 * 14 so a fill actually populates that chart's "14d" and "28d" views
 * instead of leaving most of the window empty. */
export const DevFillDays = z.union([z.literal(6), z.literal(14)]);
export const DevFillLensRequest = z.object({
    lens: DevFillLens,
    days: DevFillDays.optional(),
});
export const DevFillLensResponse = z.object({
    ok: z.boolean(),
    userId: z.string(),
    lens: DevFillLens,
    persona: z.string(),
    days: DevFillDays,
    tracking: z
        .object({
        doses: z.number().int().nonnegative(),
        weights: z.number().int().nonnegative(),
    })
        .nullable(),
    signals: z.number().int().nonnegative(),
    heartbeatTick: z.string(),
});
export const DevFillGlp1Response = DevFillLensResponse;
/** "Seed everything" — every lens except glp1 in one write, at 7 or 14 days. */
export const DevFillAllLensesRequest = z.object({
    days: DevFillDays.optional(),
});
export const DevFillAllLensesResponse = z.object({
    ok: z.boolean(),
    userId: z.string(),
    days: DevFillDays,
    signals: z.number().int().nonnegative(),
    heartbeatTick: z.string(),
});
export const HeartbeatWhyLensTraceItem = z
    .object({
    lens: z.string().optional(),
    narrative: z.string().optional(),
    facts: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
export const MeHeartbeatWhyResponse = z.object({
    ok: z.boolean(),
    decision: z
        .object({
        decisionId: z.string(),
        tickedAt: z.string(),
        cohort: z.string(),
        window: z.string().nullable(),
        lensTrace: z.array(HeartbeatWhyLensTraceItem),
        decisionFrame: z.record(z.string(), z.unknown()).nullable(),
        llmInvoked: z.boolean(),
        shortCircuitReason: z.string().nullable(),
        agentAction: z.string(),
        agentRationale: z.string().nullable(),
        escalated: z.boolean(),
        decisionPropensity: z.string().nullable(),
        fired: z.boolean(),
        versions: z.record(z.string(), z.unknown()).nullable(),
    })
        .nullable(),
    verdict: z
        .object({
        verdict: z.string().nullable(),
        observedDelta: z.string().nullable(),
        verifiedAt: z.string().nullable(),
    })
        .nullable(),
});
export const MeArmPerformanceResponse = z.object({
    since: z.string(),
    delivered: z.number().int(),
    arms: z.array(z.object({
        rule: z.string(),
        assignment: z.string(),
        n: z.number().int(),
        scored: z.number().int(),
        reward: z.number().nullable(),
    })),
});
export const DevResetResponse = z.object({
    ok: z.boolean(),
    userId: z.string(),
    cleared: z.record(z.string(), z.unknown()),
});
export const DemoTickRequest = z.object({
    scenario: z.string().optional(),
    userId: z.string().optional(),
});
export const DemoTickResponse = z.record(z.string(), z.unknown());
export const DemoSeedRequest = z.object({
    persona: z.string().optional(),
    lifeContext: z.enum(["default", "low_agency"]).optional(),
});
export const DemoSeedResponse = z.record(z.string(), z.unknown());
export const DemoExperienceDayRequest = z.object({
    day: z.number().int().min(1).max(365),
    generateBrief: z.boolean().default(false),
});
export const DemoExperienceDayResponse = z.object({
    ok: z.boolean(),
    userId: z.string(),
    day: z.number().int(),
    signals: z.number().int(),
    brief: z.object({
        status: z.string(),
        briefId: z.string().nullable(),
    }),
});
export const DemoSignalPreset = z.enum([
    "big_walk",
    "low_movement",
    "packed_calendar",
    "stress_stack",
    "short_sleep",
    "protein_meal",
    "hydration",
    "low_fluids",
    "bp_spike",
    "glucose_spike",
    "medication_dose",
    "missed_dose",
    "high_sodium",
]);
export const DemoSignalRequest = z.object({
    preset: DemoSignalPreset,
    day: z.number().int().min(1).max(7).optional(),
    tick: z.boolean().default(true),
});
export const DemoSignalResponse = z.object({
    ok: z.boolean(),
    userId: z.string(),
    preset: DemoSignalPreset,
    day: z.number().int(),
    inserted: z.number().int(),
    heartbeatTick: z.string(),
    brief: z
        .object({
        status: z.string(),
        briefId: z.string().nullable(),
    })
        .nullable(),
});
export const DemoHeartbeatStoryRequest = z.object({
    // The model-era stories staged content cards; the quiet tick is the one
    // path left that the rule engine does not own.
    story: z.enum(["quiet_log"]).default("quiet_log"),
});
export const DemoHeartbeatStoryResponse = z.object({
    ok: z.boolean(),
    userId: z.string(),
    story: z.string(),
    day: z.number().int(),
    signals: z.number().int().nonnegative(),
    surface: z.object({
        action: z.string(),
        kind: z.string(),
        title: z.string(),
    }),
    decisionId: z.string(),
});
export const DemoMomentCardRequest = z.object({
    capability: z.string().min(1).max(40),
    /** Stage an outdoor walk with a podcast episode for the way, as one walk a day gets. */
    withEpisode: z.boolean().optional(),
});
export const DemoMomentCardResponse = z.object({
    ok: z.boolean(),
    capability: z.string(),
    rung: z.number(),
    action: z.string(),
    title: z.string(),
});
/** The member's load forecast for today, structure only, never meeting titles. */
export const RuleForecastResponse = z.object({
    /** What the mode can read the member from. Neither means it can never speak. */
    sources: z.object({ wearable: z.boolean(), checkins: z.boolean() }).optional(),
    blocks: z.number(),
    forecast: z
        .object({
        meetingMinutes: z.number(),
        blockCount: z.number(),
        longestRun: z.number(),
        recoveryDebt: z.number(),
        gaps: z.array(z.object({ startMinutes: z.number(), durationMinutes: z.number() })),
    })
        .nullable(),
});
export const ConsumeAmbientRequest = z.object({
    outcome: z.enum(["accepted", "acted", "dismissed", "unknown"]).default("unknown"),
    decisionId: z.string().uuid().optional(),
    kind: z.string().max(40).optional(),
});
export const GetProfileResponse = z.record(z.string(), z.unknown());
// Every timezone here lands in a column interpolated into fleet-wide AT TIME
// ZONE queries, so an unresolvable zone is rejected at the write.
export const UpdateProfileRequest = z.object({
    currentStep: z.string().optional(),
    data: timezoneCheckedProfileData.optional(),
    persona: z.string().optional(),
    glp1CadenceDays: z.number().optional(),
    glp1StartDate: z.string().optional(),
    timezone: ianaTimezoneSchema.optional(),
});
export const UpdateProfileResponse = z.record(z.string(), z.unknown());
export const PersonaItem = z.object({
    id: z.string(),
    enabled: z.boolean(),
    seedable: z.boolean(),
});
export const GetPersonasResponse = z.object({
    personas: z.array(PersonaItem),
});
export const ReflectionResponse = z.record(z.string(), z.unknown());
export const WeatherResponse = z.record(z.string(), z.unknown());
export const WeatherTodayQuery = z.object({
    lat: z.coerce.number().min(-90).max(90).optional(),
    lon: z.coerce.number().min(-180).max(180).optional(),
    tz: z.string().optional(),
});
export const WellbeingPulseRequest = z.object({
    // The five Thrive behaviour statements, each 1-5 agreement, all in one sitting.
    answers: pulseAnswersSchema.refine((v) => PULSE_BEHAVIOURS.every((id) => v[id] !== undefined), {
        message: "Answer every behaviour before submitting the pulse",
    }),
});
export const WellbeingPulseShift = z.object({
    id: pulseBehaviourSchema,
    baseline: z.number(),
    current: z.number(),
    delta: z.number(),
});
export const WellbeingPulseResponse = z.object({
    accepted: z.boolean(),
    // Mean agreement across the five behaviours, 1-5.
    mean: z.number().nullable(),
    // Movement per behaviour since the reading being compared against. Empty
    // when this is the member's first.
    shifts: z.array(WellbeingPulseShift),
    // What the shifts compare against: the previous check-in, else the
    // onboarding baseline. Null on a first reading.
    comparedTo: z
        .object({
        mean: z.number().nullable(),
        occurredAt: z.string().nullable(),
        source: z.enum(["previous", "baseline"]),
    })
        .nullable(),
    occurredAt: z.string(),
});
export const DayZeroComprehensionRequest = z.object({
    understands: z.boolean(),
});
export const DayZeroComprehensionResponse = z.object({
    accepted: z.boolean(),
    understands: z.boolean(),
    answeredAt: z.string(),
});
export const DayZeroStateResponse = z.object({
    // True once the member has taken at least one stop. Until then the Today
    // card owns the first hop and no toast should appear anywhere.
    started: z.boolean(),
    // The stop the member is on, or null when the tour is over.
    stop: z
        .object({
        id: dayZeroStopIdSchema,
        key: z.string(),
        route: z.string(),
        title: z.string(),
        body: z.string(),
        action: z.string(),
    })
        .nullable(),
});
export const DayZeroSkipResponse = z.object({ ok: z.boolean() });
export const DayZeroResetResponse = z.object({
    ok: z.boolean(),
    cleared: z.object({
        stops: z.array(dayZeroStopIdSchema),
        comprehension: z.boolean(),
    }),
});
export const PlanIdParams = z.object({ planId: z.string() });
export const GetPlanStatsResponse = z.object({
    planId: z.string(),
    totalMicrosteps: z.number(),
    completed: z.number(),
    adherenceRate: z.number(),
});
export const StartPlanResponse = z.object({
    planId: z.string(),
    status: z.string(),
});
export const UpdatePlanProgressRequest = z.object({
    note: z.string().nullable().optional(),
});
export const UpdatePlanProgressResponse = z.record(z.string(), z.unknown());
export const GeneratePlanResponse = z.record(z.string(), z.unknown());
export const OperationsResponse = z.record(z.string(), z.unknown());
export const CohortResponse = z.record(z.string(), z.unknown());
//# sourceMappingURL=companion.js.map